import React, { useState, useEffect, useCallback } from 'react';
import { C } from '../constants/theme';
import { Table } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { Modal } from '../components/Modal';
import { FormField, inputStyle } from '../components/FormField';
import { Button } from '../components/Button';
import { Badge, BoolBadge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { addOrUpdate, getUsers } from '../utils/UserService';
import { Pencil } from 'lucide-react';

const cardStyle = {
    background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
    boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
};

const INIT_FORM = {
    firstName: '', lastName: '', userName: '', email: '',
    mobile: '', password: '', status: 1,
};

export function UserModule() {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(null);
    const [detailRow, setDetailRow] = useState(null);
    const [form, setForm] = useState(INIT_FORM);
    const [saving, setSaving] = useState(false);
    const [filters, setFilters] = useState([]);
    const [sortingColumn, setSortingColumn] = useState('id');
    const [direction, setDirection] = useState('ASC');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getUsers({
                pageNumber: page, pageSize, sortingColumn, direction, filters
            });
            const obj = res?.responseObject;
            if (!obj) return;
            setData(obj.content);
            setTotal(obj.totalElements);
            setTotalPages(obj.totalPages);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, sortingColumn, direction, filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openAdd = () => { setForm(INIT_FORM); setModal('add'); };

    const openEdit = (row) => {
        setForm({
            id: row.id,
            firstName: row.firstName || '',
            lastName: row.lastName || '',
            userName: row.userName || '',
            email: row.email || '',
            mobile: row.mobile || '',
            password: '',
            status: row.status ?? 1,
        });
        setModal(row);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await addOrUpdate(form);
            setModal(null);
            fetchData();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { key: 'id', label: 'ID' },
        {
            key: 'firstName', label: 'Name',
            render: (val, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—'
            , viewEnable: true
        },
        { key: 'userName', label: 'Username' },
        { key: 'email', label: 'Email' },
        { key: 'mobile', label: 'Mobile' },
        {
            key: 'provider', label: 'Provider',
            render: (val) => (
                <span style={{
                    display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                    fontSize: 11, fontWeight: 600,
                    background: val === 'local' ? '#E3F2FD' : '#F3E8FF',
                    color: val === 'local' ? '#1565C0' : '#6C3FC5',
                }}>
                    {val === 'local' ? '🔑 Local' : `🌐 ${val || '—'}`}
                </span>
            )
        },
        {
            key: 'emailVerified', label: 'Email Verified',
            render: (val) => <BoolBadge val={val} />
        },
        {
            key: 'mobileVerified', label: 'Mobile Verified',
            render: (val) => <BoolBadge val={val} />
        },
        {
            key: 'status', label: 'Status',
            render: (val) => <Badge status={val} />
        },
    ];

    return (
        <div>
            <PageHeader title="Users" count={total} onAdd={openAdd} />
            <div style={cardStyle}>
                <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
                    <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Search users…" />
                </div>
                {loading ? <LoadingSpinner /> : (
                    <Table columns={columns} data={data}
                        onCellClick={(col, row) => col.viewEnable && setDetailRow(row)}
                        actions={(row) => [
                            <Button key="e" small variant="outline" onClick={() => openEdit(row)}>
                                <Pencil size={13} strokeWidth={2} />
                            </Button>
                        ]} />
                )}
                <Pagination
                    page={page} totalPages={totalPages} totalElements={total} pageSize={pageSize}
                    onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                />
            </div>

            {/* Detail Modal */}
            {detailRow && (
                <Modal title={`User — ${detailRow.firstName || ''} ${detailRow.lastName || ''}`} onClose={() => setDetailRow(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13 }}>
                        {[
                            ['ID', detailRow.id],
                            ['Username', detailRow.userName],
                            ['Email', detailRow.email],
                            ['Mobile', detailRow.mobile],
                            ['Provider', detailRow.provider],
                            ['Status', <Badge status={detailRow.status} />],
                            ['Email Verified', <BoolBadge val={detailRow.emailVerified} />],
                            ['Mobile Verified', <BoolBadge val={detailRow.mobileVerified} />],
                        ].map(([k, v]) => (
                            <div key={k} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
                                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
                                <div style={{ color: C.text }}>{v ?? '—'}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                        <Button variant="ghost" onClick={() => setDetailRow(null)}>Close</Button>
                    </div>
                </Modal>
            )}

            {/* Add / Edit Modal */}
            {modal && (
                <Modal
                    title={modal === 'add' ? 'Add User' : 'Edit User'}
                    onClose={() => setModal(null)}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                        <FormField label="First Name" required>
                            <input style={inputStyle} value={form.firstName} autoFocus
                                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                                placeholder="e.g. John" />
                        </FormField>

                        <FormField label="Last Name">
                            <input style={inputStyle} value={form.lastName}
                                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                                placeholder="e.g. Doe" />
                        </FormField>

                        <FormField label="Username" required>
                            <input style={inputStyle} value={form.userName}
                                onChange={(e) => setForm((f) => ({ ...f, userName: e.target.value }))}
                                placeholder="e.g. johndoe" />
                        </FormField>

                        <FormField label="Email" required>
                            <input style={inputStyle} type="email" value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder="e.g. john@example.com" />
                        </FormField>

                        <FormField label="Mobile">
                            <input style={inputStyle} type="tel" value={form.mobile}
                                onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                                placeholder="e.g. 9876543210" />
                        </FormField>

                        <FormField label="Password" required={modal === 'add'}>
                            <input style={inputStyle} type="password" value={form.password}
                                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                placeholder={modal === 'add' ? '••••••••' : 'Leave blank to keep same'} />
                        </FormField>

                        <FormField label="Status" required>
                            <select style={inputStyle} value={form.status}
                                onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}>
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </select>
                        </FormField>

                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button onClick={handleSave}
                            disabled={!form.firstName || !form.userName || !form.email || (modal === 'add' && !form.password) || saving}>
                            {saving ? 'Saving…' : modal === 'add' ? 'Create User' : 'Update User'}
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}